/**
 * cart-drawer-extras.js
 *
 * Maneja las interacciones del cart drawer custom (Lejan-style clone):
 *   - Botones +/- de cantidad
 *   - Botón trash (remove item)
 *   - ADD TO CART en el upsell "Complete your order"
 *   - Actualiza el free shipping bar en vivo
 *
 * Trabaja con la API de Dawn (cart-drawer, cart-drawer-items custom elements,
 * /cart/change.js, /cart/add.js) y dispara section rendering para refresh.
 */

(function () {
  'use strict';

  const SECTIONS_TO_RENDER = 'cart-drawer,cart-icon-bubble';

  // ---------- Helpers ----------

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function openCartDrawer() {
    const drawer = getCartDrawer();
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  async function refreshCartSections() {
    // Re-render via Section Rendering API.
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}?sections=${SECTIONS_TO_RENDER}`;
      const resp = await fetch(url, { credentials: 'same-origin' });
      const json = await resp.json();
      const parser = new DOMParser();

      // cart-drawer: preserve the <cart-drawer> custom element itself across
      // re-render — replace only its INNER content, not the wrapping section.
      // This keeps the .active class, event listeners, and ARIA state stable
      // so the drawer remains visually open when cart becomes empty after
      // trash click, exactly like doreandrose.com does.
      if (json['cart-drawer']) {
        const doc = parser.parseFromString(json['cart-drawer'], 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        const oldDrawer = document.querySelector('cart-drawer');

        if (oldDrawer && newDrawer) {
          // Sync is-empty class with the new server-rendered state
          oldDrawer.classList.toggle(
            'is-empty',
            newDrawer.classList.contains('is-empty')
          );
          // Replace only the children, preserving the wrapper element and its
          // open state (.active, open attribute, body lock, etc.)
          oldDrawer.innerHTML = newDrawer.innerHTML;
        } else if (newDrawer) {
          // Fallback: cart-drawer doesn't exist yet, do full section swap
          const oldEl = document.getElementById('shopify-section-cart-drawer');
          const newEl = doc.querySelector('#shopify-section-cart-drawer');
          if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
        }
      }

      // Safety net: if no .active drawer exists, ensure body isn't locked.
      // Prevents the page-blocked bug under any unexpected state.
      if (!document.querySelector('cart-drawer.active')) {
        document.body.classList.remove('overflow-hidden');
      }

      // Free shipping bar celebration: el .cart__message renderiza SIN .is-success
      // (con data-met="true" cuando se cumple el threshold) para que las CSS transitions
      // de los dots disparen al añadir la clase. Sin esto, el elemento nace con
      // .is-success ya puesto via innerHTML swap y los pseudos van directos a su
      // estado final sin animar.
      triggerCelebrationIfMet();

      // cart-icon-bubble: inline en header.liquid, NO tiene wrapper
      // #shopify-section-cart-icon-bubble. Hay que reemplazar el inner del
      // <a id="cart-icon-bubble"> directamente con el inner del shopify-section
      // que devuelve la API.
      if (json['cart-icon-bubble']) {
        const doc = parser.parseFromString(json['cart-icon-bubble'], 'text/html');
        const wrapper = doc.querySelector('#shopify-section-cart-icon-bubble') || doc.body;
        const targetA = document.getElementById('cart-icon-bubble');
        if (targetA && wrapper) {
          targetA.innerHTML = wrapper.innerHTML;
        }
      }
    } catch (err) {
      console.error('[cart-drawer-extras] section refresh failed', err);
    }
  }

  // Celebrate once per threshold-crossing event. Estado persistido en
  // sessionStorage para que sobreviva section refreshes pero NO una nueva visita.
  // Comportamiento:
  //   - Below → Above:   dispara animación + setea flag
  //   - Above → Above:   NO redispara (flag activa)
  //   - Above → Below:   limpia flag (lista para próxima celebración)
  //   - Below → Above otra vez: dispara (flag estaba limpia)
  const CELEBRATED_KEY = 'klassyFreeShippingCelebrated';
  function triggerCelebrationIfMet() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const msg = drawer.querySelector('.cart__message');
    if (!msg) return;
    const met = msg.dataset.met === 'true';
    let alreadyCelebrated = false;
    try { alreadyCelebrated = sessionStorage.getItem(CELEBRATED_KEY) === '1'; } catch (_) {}

    if (!met) {
      // Cart por debajo del threshold → limpiar flag para que la próxima cruz dispare
      try { sessionStorage.removeItem(CELEBRATED_KEY); } catch (_) {}
      return;
    }

    if (alreadyCelebrated) {
      // Ya celebrado en esta sesión; solo aplicar la clase visualmente sin animar.
      // CSS transitions no disparan al añadir clase a un elemento que ACABA de
      // montarse via innerHTML swap, así que setearla directa = estado final sin anim.
      if (!msg.classList.contains('is-success')) msg.classList.add('is-success');
      return;
    }

    // First-time crossing → marcar flag + animar (double rAF para que transitions arranquen)
    try { sessionStorage.setItem(CELEBRATED_KEY, '1'); } catch (_) {}
    if (msg.classList.contains('is-success')) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        msg.classList.add('is-success');
      });
    });
  }

  function flashError(msg) {
    const errEl = document.getElementById('CartDrawer-CartErrors');
    if (errEl) {
      errEl.textContent = msg;
      errEl.style.opacity = '1';
      setTimeout(() => { errEl.textContent = ''; }, 3000);
    } else {
      console.warn('[cart-drawer-extras]', msg);
    }
  }

  function setLoading(el, loading) {
    if (!el) return;
    if (loading) el.setAttribute('data-loading', 'true');
    else el.removeAttribute('data-loading');
  }

  // ---------- Quantity update ----------

  async function changeItemQuantity(key, quantity) {
    // Capture drawer-open state BEFORE the AJAX so we can restore it after
    // re-render — protects against any code path that may destroy the
    // <cart-drawer> element and reset its classes.
    const drawerWasActive = !!document.querySelector('cart-drawer.active');
    const drawerWasOpenAttr = !!document.querySelector('cart-drawer[open]');

    try {
      const resp = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      await refreshCartSections();

      // Restore open state after refresh (regardless of how refresh did its work)
      if (drawerWasActive || drawerWasOpenAttr) {
        const d = document.querySelector('cart-drawer');
        if (d) {
          if (drawerWasActive) d.classList.add('active');
          if (drawerWasOpenAttr) d.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    } catch (err) {
      flashError('Could not update cart');
      console.error('[cart-drawer-extras] change error', err);
    }
  }

  // ---------- Upsell add ----------

  async function addItemToCart(variantId, btn) {
    if (!variantId) return;

    // Warm the cart-line-item-sized image NOW (parallel with /cart/add) so that
    // when the drawer re-renders, the new line's photo is already cached and
    // appears instantly instead of fetching lazily (3-4s flash). The upsell card
    // already shows the product image at a different width → different CDN URL,
    // so we request the exact widths the cart line uses (200/240).
    try {
      const card = btn && btn.closest('.cart-upsell-item');
      const cardImg = card && card.querySelector('img');
      const base = cardImg && (cardImg.currentSrc || cardImg.src);
      if (base) {
        [240, 200, 120].forEach((w) => {
          const u = /[?&]width=\d+/.test(base)
            ? base.replace(/([?&]width=)\d+/, '$1' + w)
            : base + (base.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
          const pre = new Image();
          pre.src = u;
        });
      }
    } catch (_) { /* no-op */ }

    // Loading state soft — locker width + sustituir texto por "...".
    // Pattern del antiguo _klassyUpsellAdd que Arturo prefería visualmente.
    let originalText = null;
    if (btn) {
      btn.disabled = true;
      btn.style.minWidth = btn.offsetWidth + 'px';
      originalText = btn.textContent;
      btn.textContent = '...';
      setLoading(btn, true);
    }
    // Suavizar el swap: pequeño fade-out del drawer body antes del innerHTML replace.
    const drawerBody = document.querySelector('cart-drawer .cart-drawer-lejan__body');
    if (drawerBody) {
      drawerBody.style.transition = 'opacity 0.15s ease';
      drawerBody.style.opacity = '0.6';
    }
    try {
      const resp = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.description || `HTTP ${resp.status}`);
      }
      await refreshCartSections();
      // Fade back in (el drawerBody original ya no existe tras swap; busco el nuevo)
      const newBody = document.querySelector('cart-drawer .cart-drawer-lejan__body');
      if (newBody) {
        newBody.style.opacity = '0';
        newBody.style.transition = 'opacity 0.25s ease';
        requestAnimationFrame(() => { newBody.style.opacity = '1'; });
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
      openCartDrawer();
    } catch (err) {
      flashError(err.message || 'Could not add to cart');
      console.error('[cart-drawer-extras] add error', err);
      // Restore en caso de error
      if (drawerBody) drawerBody.style.opacity = '1';
      if (btn && originalText !== null) {
        btn.textContent = originalText;
        btn.style.minWidth = '';
        btn.disabled = false;
        setLoading(btn, false);
      }
    }
    // Nota: el botón original puede no existir tras innerHTML swap.
    // Si existe (no fue reemplazado), restauramos. Si fue reemplazado, el HTML
    // server ya viene con el texto correcto.
    if (btn && btn.isConnected && originalText !== null) {
      btn.textContent = originalText;
      btn.style.minWidth = '';
      btn.disabled = false;
      setLoading(btn, false);
    }
  }

  // ---------- Delegated event listeners (drawer scope) ----------

  document.addEventListener('click', async (e) => {
    const drawer = e.target.closest('cart-drawer');
    if (!drawer) return;

    // QTY decrease
    const dec = e.target.closest('[data-cart-qty-decrease]');
    if (dec) {
      e.preventDefault();
      const key = dec.dataset.itemKey;
      const input = drawer.querySelector(`[data-cart-qty-input][data-item-key="${CSS.escape(key)}"]`);
      const current = parseInt(input?.value || '1', 10);
      const next = Math.max(0, current - 1);
      input.value = next;
      changeItemQuantity(key, next);
      return;
    }

    // QTY increase
    const inc = e.target.closest('[data-cart-qty-increase]');
    if (inc) {
      e.preventDefault();
      const key = inc.dataset.itemKey;
      const input = drawer.querySelector(`[data-cart-qty-input][data-item-key="${CSS.escape(key)}"]`);
      const next = parseInt(input?.value || '0', 10) + 1;
      input.value = next;
      changeItemQuantity(key, next);
      return;
    }

    // Remove
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      const key = remove.dataset.itemKey;
      changeItemQuantity(key, 0);
      return;
    }

    // Upsell ADD TO CART
    const upsellBtn = e.target.closest('[data-upsell-add]');
    if (upsellBtn) {
      e.preventDefault();
      const variantId = upsellBtn.dataset.variantId;
      addItemToCart(variantId, upsellBtn);
      return;
    }

    // Upsell toggle — abre el variant picker (.is-open)
    const upsellToggle = e.target.closest('[data-upsell-toggle]');
    if (upsellToggle) {
      e.preventDefault();
      const card = upsellToggle.closest('.cart-upsell-item');
      const variants = card && card.querySelector('.cart-upsell-item__variants');
      if (variants) variants.classList.toggle('is-open');
      return;
    }
  });

  // Initial page load: si el cart ya superó el threshold (drawer renderiza con
  // data-met="true"), disparar la animación de celebración en el primer paint.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', triggerCelebrationIfMet);
  } else {
    triggerCelebrationIfMet();
  }

  // Qty input direct edit
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    const key = input.dataset.itemKey;
    const qty = Math.max(0, parseInt(input.value || '0', 10));
    changeItemQuantity(key, qty);
  });
})();

// cache-bust 1778829345
